import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import useRect from '@jimmyboys/liita-ui/dist/hooks/useRect'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useCalculateTextsHeights from '@jimmyboys/liita-ui/dist/hooks/useCalculateTextsHeights'
import { useLocation } from 'react-router-dom'

import { useParams } from 'react-router-dom'

import { useFetchList } from '../../store'

import { Comment } from 'models'
import CommentThreadCard, {
	CommentThreadCardSkeleton,
} from 'components/cards/CommentThreadCard/CommentThreadCard'
import EpisodesSeparatorCard from 'components/cards/EpisodesSeparatorCard/EpisodesSeparatorCard'

import NoData from 'components/general/NoData/NoData'

import { calculateItemHeight, LINE_SPLIT_REGEX } from './utils'

import themeStyles from 'assets/styles/theme.module.sass'

import styles from './ClinicalProcess.module.sass'
import useCollectionSpecs, {
	getThemeSpacing,
	getThemeVariable,
} from 'hooks/useCollectionSpecs'
import { useRecoilValue } from 'recoil'
import {
	currentDetailLevelState,
	expandedCommentsAtom,
	searchProcessString,
} from 'store/atoms'
import useTermSearch from 'hooks/useTermSearch'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'

const { phi, smallFontSize } = themeStyles
const [, themeHorizontalSpacing] = getThemeSpacing()
const themeSmallFontSize = getThemeVariable(smallFontSize)

const ClinicalCommentsController = () => {
	const { i18n, screenSize } = useGlobal()
	const collectionRef = useRef(null)
	const rect = useRect(collectionRef)
	const { clinicalProcessId } = useParams()
	const { dispatch } = useDrawerContext()
	const location = useLocation()
	const [, , setSearchParams] = useQueryParams(location)

	const processString = useRecoilValue(searchProcessString)
	const detailLevel = useRecoilValue(currentDetailLevelState)
	const allExpanded = useRecoilValue(expandedCommentsAtom)
	const [expandedCards, setExpandedCards] = useState(new Set())
	const prevAllExpanded = useRef(allExpanded)

	const cards = {
		CommentThreadCard,
		CommentThreadCardSkeleton,
		EpisodesSeparatorCard,
	}
	const cardLabels = {
		author: i18n`Author`,
		date: i18n`Date`,
		commentTitle: i18n`Commentary`,
		commentary: i18n`Commentary`,
		documents: i18n`Documents`,
		by: i18n`by`,
	}

	const commentsUri = `/clinical_processes/${clinicalProcessId}/comments`

	const { state, contents } = useFetchList([commentsUri], [Comment.fromJSON])
	const isLoading = state === 'loading'

	const [commentsData] = contents || []

	const cardLayout = 'vertical'
	const comments = (commentsData || []).map((json) => {
		return {
			...json,
			sortParam: json.isPinned,
			$cardName: 'CommentThreadCard',
			$cardLabels: cardLabels,
			$layout: cardLayout,
			$detailLevel: detailLevel,
		}
	})

	const pinnedComments = comments
		.filter((comment) => comment.isPinned)
		.sort(
			(a, b) =>
				b.latestUpdateDate.timestamp - a.latestUpdateDate.timestamp
		)

	const notPinnedComments = comments
		.filter((comment) => !comment.isPinned)
		.sort(
			(a, b) =>
				b.latestUpdateDate.timestamp - a.latestUpdateDate.timestamp
		)

	const data = useMemo(
		() => [...pinnedComments, ...notPinnedComments],
		[pinnedComments, notPinnedComments]
	)

	const isCommentExpanded = useCallback(
		(comment) => expandedCards.has(comment.id),
		[expandedCards]
	)

	const toggleCommentExpand = useCallback((commentId) => {
		setExpandedCards((prev) => {
			const next = new Set(prev)
			if (next.has(commentId)) {
				next.delete(commentId)
			} else {
				next.add(commentId)
			}
			return next
		})
	}, [])

	useEffect(() => {
		if (prevAllExpanded.current === allExpanded) return
		prevAllExpanded.current = allExpanded
		if (allExpanded) {
			setExpandedCards(
				new Set(
					data
						.filter((comment) => (comment.repliesCount ?? 0) > 0)
						.map((comment) => comment.id)
				)
			)
		} else {
			setExpandedCards(new Set())
		}
	}, [allExpanded, data])

	const expandedReplyUris = useMemo(() => {
		return data
			.filter(
				(comment) =>
					isCommentExpanded(comment) &&
					(comment.repliesCount ?? 0) > 0
			)
			.map(
				(comment) =>
					`/clinical_processes/${clinicalProcessId}/comments/${comment.id}/replies`
			)
	}, [data, clinicalProcessId, isCommentExpanded])

	const replyMaps = useMemo(
		() => expandedReplyUris.map(() => Comment.fromJSON),
		[expandedReplyUris]
	)

	const { contents: repliesContents } = useFetchList(
		expandedReplyUris.length > 0 ? expandedReplyUris : ['__disabled__'],
		expandedReplyUris.length > 0 ? replyMaps : [Comment.fromJSON]
	)

	const repliesByCommentId = useMemo(() => {
		const map = {}
		expandedReplyUris.forEach((uri, index) => {
			const commentIdMatch = uri.match(/\/comments\/(\d+)\/replies/)
			if (commentIdMatch) {
				const commentId = parseInt(commentIdMatch[1], 10)
				const replies = repliesContents?.[index]
				if (Array.isArray(replies)) {
					map[commentId] = replies
				}
			}
		})
		return map
	}, [expandedReplyUris, repliesContents])

	const textSpecs = useCollectionSpecs(
		collectionRef,
		rect,
		screenSize === 'small'
			? themeHorizontalSpacing * (phi * 2) * 2
			: themeHorizontalSpacing * (phi * 4) * 2,
		themeSmallFontSize
	)
	const cardMargin = 0

	const isBeforeTextComputation = !(textSpecs.fontSize && textSpecs.maxWidth)

	const textsHeights = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : data,
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX
	)

	const { searchMatches, setQuery } = useTermSearch(data, [
		'body',
		'author.name',
	])

	useEffect(() => {
		setQuery(processString)
	}, [processString, setQuery])

	let collectionItems = []
	let itemsHeights = []
	if (isLoading) {
		const loadingItems = new Array(5).fill({})
		collectionItems = loadingItems.map(() => {
			return {
				$cardName: 'CommentThreadCardSkeleton',
				$layout: screenSize === 'small' ? 'vertical' : 'horizontal',
			}
		})
		itemsHeights = loadingItems.map(() => {
			return cardLayout === 'vertical' &&
				CommentThreadCardSkeleton.verticalDefaultHeight
				? CommentThreadCardSkeleton.verticalDefaultHeight +
						(parseFloat(styles.rowMargin) || cardMargin)
				: CommentThreadCardSkeleton.defaultHeight +
						(parseFloat(styles.rowMargin) || cardMargin)
		})
	} else {
		itemsHeights = []
		collectionItems = []
		searchMatches.forEach((item) => {
			const [height, textHeight] = calculateItemHeight(
				item,
				textsHeights,
				textSpecs.lineHeight,
				cardMargin,
				cards,
				detailLevel,
				screenSize
			)
			itemsHeights.push(height)
			collectionItems.push({
				...item,
				height,
				textHeight,
			})
		})
	}

	const clickItem = useCallback(
		(data) => {
			setSearchParams({ comment: data.id })
			dispatch({
				action: 'OPEN_DRAWER',
				payload: {
					data: { processId: data.processId, id: data.id },
					type: 'CommentThreadDrawer',
				},
			})
		},
		[dispatch, setSearchParams]
	)

	return (
		<>
			{!isLoading && !collectionItems.length ? (
				<NoData
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={i18n`This process has no **comments** yet.`}
				/>
			) : null}
			{(searchMatches ?? []).map((item) => (
				<CommentThreadCard
					key={item.id}
					data={item}
					expanded={isCommentExpanded(item)}
					onToggleExpand={() => toggleCommentExpand(item.id)}
					replies={repliesByCommentId[item.id]}
					onClick={(event) => {
						event.preventDefault()
						clickItem(item)
					}}
				/>
			))}
		</>
	)
}

export default ClinicalCommentsController
